import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { useState } from 'react';
import { searchUsers } from './suggest_api';
import { SuggestInput } from './SuggestInput';

/** The credited author as the editors track it; only the userid is saved. */
export type EditorAuthor = {
   userid: number;
   /** Display name, e.g. "username (@handle)". */
   label: string;
   imageUrl: string | null;
};

/**
 * The staged author control shared by the wiki and guide editors. A page
 * has exactly one author, so the control is structurally single-valued:
 * with an author picked only their row shows, and the row's remove button
 * swaps in the user search to pick the replacement — the staged author
 * stays in place until the new one is chosen, so there is never an
 * author-less moment. A revert button appears once the pick differs from
 * the loaded author. Nothing here saves on its own — the page's Save
 * action diffs the pick against `initial`.
 */
export function AuthorPicker({
   queryKey,
   initial,
   value,
   onChange,
}: {
   /** Cache-separates this picker from other SuggestInputs on the page. */
   queryKey: string;
   /** The author as the page loaded; the revert button restores it. */
   initial: EditorAuthor | null;
   value: EditorAuthor | null;
   onChange: (author: EditorAuthor | null) => void;
}) {
   const [query, setQuery] = useState('');
   // The remove button opens the search; picking (or Cancel) closes it.
   const [searching, setSearching] = useState(false);
   const hasChanges = value?.userid !== initial?.userid;
   const searchOpen = searching || value === null;

   return (
      <div>
         {value && (
            <div className="flex items-center gap-2 rounded-lg border border-gray-300 p-1.5 text-sm">
               {value.imageUrl ? (
                  <img
                     src={value.imageUrl}
                     alt=""
                     className="size-6 shrink-0 rounded-full object-cover"
                  />
               ) : (
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                     <FaIcon name="user" className="size-3.5" aria-hidden />
                  </span>
               )}
               <span className="min-w-0 flex-1 truncate">{value.label}</span>
               {hasChanges && (
                  <button
                     type="button"
                     className="flex size-7 shrink-0 items-center justify-center rounded text-gray-400 outline-none hover:bg-gray-100 hover:text-gray-600 focus-visible:ring-[3px] focus-visible:ring-ring/50"
                     onClick={() => {
                        onChange(initial);
                        setSearching(false);
                     }}
                     aria-label={_js('Revert author change')}
                     title={_js('Revert author change')}
                  >
                     <FaIcon name="arrow-rotate-left" className="size-3.5" aria-hidden />
                  </button>
               )}
               {!searchOpen && (
                  <button
                     type="button"
                     className="flex size-7 shrink-0 items-center justify-center rounded text-gray-400 outline-none hover:bg-gray-100 hover:text-red-600 focus-visible:ring-[3px] focus-visible:ring-ring/50"
                     onClick={() => setSearching(true)}
                     aria-label={_js('Change author')}
                     title={_js('Change author')}
                  >
                     <FaIcon name="trash-can" className="size-3.5" aria-hidden />
                  </button>
               )}
            </div>
         )}
         {searchOpen && (
            <div className="mt-2 flex items-start gap-2">
               <SuggestInput
                  queryKey={queryKey}
                  className="min-w-0 flex-1"
                  value={query}
                  onValueChange={setQuery}
                  placeholder={_js('Search for a user...')}
                  ariaLabel={_js('Search for a user')}
                  autoFocus={value !== null}
                  fetchSuggestions={searchUsers}
                  imageShape="avatar"
                  minQueryLength={1}
                  excludeKeys={value ? [value.userid] : []}
                  onSelect={suggestion => {
                     // searchUsers labels are already "username (@handle)", so
                     // the label is the display name; only the userid is saved.
                     onChange({
                        userid: Number(suggestion.key),
                        label: suggestion.label,
                        imageUrl: suggestion.imageUrl ?? null,
                     });
                     setQuery('');
                     setSearching(false);
                  }}
               />
               {value && (
                  <button
                     type="button"
                     className="h-9 shrink-0 rounded-lg px-3 text-sm text-gray-500 outline-none hover:bg-gray-100 hover:text-gray-700 focus-visible:ring-[3px] focus-visible:ring-ring/50"
                     onClick={() => {
                        setQuery('');
                        setSearching(false);
                     }}
                  >
                     {_js('Cancel')}
                  </button>
               )}
            </div>
         )}
      </div>
   );
}
